import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Button
} from '@mui/material';
import RootDialogPage from '../../Commun/components/RootDialogPage';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { customersApi, Customer } from '../services/customersApi';

interface CustomerEditDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    customer: Customer | null;
}

export const CustomerEditDialog: React.FC<CustomerEditDialogProps> = ({
    open,
    onClose,
    onSuccess,
    customer
}) => {
    const [formData, setFormData] = useState<Partial<Customer>>({
        code: '',
        name: '',
        companyName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        country: '',
        postalCode: '',
    });
    const [loading, setLoading] = useState(false);
    const { t } = useLanguage();
    

    useEffect(() => {
        if (customer) {
            setFormData({ ...customer });
        } else {
            setFormData({
                code: '',
                name: '',
                companyName: '',
                email: '',
                phone: '',
                address: '',
                city: '',
                country: '',
                postalCode: '',
            });
        }
    }, [customer, open]);

    const handleSubmit = async () => {
        if (!formData.name || !formData.code) {
            blobticMessage.warning(t('common.messages.warning'), t('maker.msg.error.required'));
            return;
        }

        try {
            setLoading(true);
            const customerData = {
                ...formData,
                companyName: formData.companyName || undefined,
                email: formData.email || undefined,
                phone: formData.phone || undefined,
                address: formData.address || undefined,
                city: formData.city || undefined,
                country: formData.country || undefined,
                postalCode: formData.postalCode || undefined,
            };

            if (customer) {
                await customersApi.update(customer.id, customerData);
                blobticMessage.success(t('common.messages.success'), t('common.messages.saved'));
            } else {
                await customersApi.create(customerData);
                blobticMessage.success(t('common.messages.success'), t('maker.msg.success.create'));
            }
            onSuccess();
            onClose();
        } catch (error: any) {
            blobticMessage.error(t('common.messages.error'), t('maker.msg.failedSave', error.message));
        } finally {
            setLoading(false);
        }
    };

    return (
        <RootDialogPage
            open={open}
            onClose={onClose}
            title={customer ? t('maker.customers.edit') : t('maker.customers.new')}
            onAction={handleSubmit}
            actionLabel={customer ? t('action.update') : t('action.create')}
            actionDisabled={loading}
            maxWidth="sm"
            fullWidth
        >
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label={t('maker.field.code')} fullWidth required value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} />
                <TextField label={t('maker.field.name')} fullWidth required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                <TextField label={t('maker.field.company')} fullWidth value={formData.companyName || ''} onChange={(e) => setFormData({ ...formData, companyName: e.target.value })} />
                <TextField label={t('maker.field.email')} type="email" fullWidth value={formData.email || ''} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                <TextField label={t('maker.field.phone')} type="tel" fullWidth value={formData.phone || ''} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                <TextField label={t('maker.field.address')} fullWidth value={formData.address || ''} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField label={t('maker.field.city')} fullWidth value={formData.city || ''} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
                    <TextField label={t('maker.field.country')} fullWidth value={formData.country || ''} onChange={(e) => setFormData({ ...formData, country: e.target.value })} />
                </Box>
                <TextField label={t('maker.field.postalCode')} fullWidth value={formData.postalCode || ''} onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })} />
            </Box>
        </RootDialogPage>
    );
};
